import { useMemo } from 'react';
import { useGetAuthorizationProfessional } from '~/domain/Credentials/usecases/useGetAuthorizationProfessional';
import { useCandidateOvertime } from '~/domain/Jobs/useCases/useCandidateOvertime';
import { useGetJob } from '~/domain/Jobs/useCases/useGetJob';
import { useJobCandidates } from './useJobCandidates';

const mergeCandidateOvertime = (baseJob, overtime) => {
  if (!overtime) {
    return baseJob;
  }

  const {
    regularAndOvertimeHours,
    totalOvertime,
    totalRegular,
    convenience_fee,
    convenience_fee_percent,
    employeeOptionFee,
    total,
    tm_fee,
    tm_fee_percent,
  } = overtime;

  return {
    ...baseJob,
    regularAndOvertimeHours,
    totalOvertime,
    totalRegular,
    convenience_fee,
    convenience_fee_percent,
    employeeOptionFee,
    total,
    tm_fee,
    tm_fee_percent,
  };
};

/**
 * Owns the React Query read sequencing for the Confirm Candidate screen: the
 * route job, candidate-scoped overtime, the candidate list, and the candidate's
 * authorization/credential data. Reads are gated so dependent queries only run
 * once `useGetJob` has returned the route job (see PR3A read-sequencing tests).
 */
export const useConfirmCandidateData = ({ jobId, candidateId }) => {
  const jobQuery = useGetJob(jobId);
  const hasLoadedJob = jobQuery.data?.id === jobId;
  const baseJob = hasLoadedJob ? jobQuery.data : undefined;

  const candidateOvertimeQuery = useCandidateOvertime(jobId, candidateId, {
    enabled: Boolean(hasLoadedJob && candidateId),
  });

  const job = useMemo(
    () =>
      candidateOvertimeQuery.data && baseJob?.id === jobId
        ? mergeCandidateOvertime(baseJob, candidateOvertimeQuery.data)
        : baseJob,
    [baseJob, candidateOvertimeQuery.data, jobId],
  );

  const {
    reloadCandidates,
    allCandidates: queryAllCandidates,
    visibleCandidates: queryVisibleCandidates,
    isLoading: loadingCandidates,
    isSuccessFetchJobCandidates,
  } = useJobCandidates({
    jobId,
    specialty: hasLoadedJob ? job?.specialty : undefined,
    enabled: Boolean(hasLoadedJob),
  });

  const allCandidates = queryAllCandidates ?? [];
  const visibleCandidates = isSuccessFetchJobCandidates ? (queryVisibleCandidates ?? []) : [];

  const hasCorrectJob = hasLoadedJob && job?.id === jobId;
  const isOnsiteInterview = hasCorrectJob && job?.isOnsiteInterview;

  const currentCounter = allCandidates?.find((counter) => counter?.candidate?.id === candidateId);
  const candidate = currentCounter?.candidate;

  const { displayProfession, authorizations, isCredentialV2 } = useGetAuthorizationProfessional({
    professionalId: candidateId ?? '',
    candidateLicense: candidate?.license || candidate?.licenses?.[0],
    jobProfessionName: job?.professionName,
    jobSubProfession: job?.subProfession,
    offerOwnerId: job?.offer_owner?.id,
    jobProfession: job?.profession,
    jobState: job?.state,
  });

  return {
    jobQuery,
    job,
    hasLoadedJob,
    hasCorrectJob,
    isOnsiteInterview,
    candidate,
    currentCounter,
    allCandidates,
    visibleCandidates,
    loadingCandidates,
    isSuccessFetchJobCandidates,
    reloadCandidates,
    displayProfession,
    authorizations,
    isCredentialV2,
  };
};
